// Beginner, part 1: reading a chart. Who won a candle, what its wicks say,
// close vs wick at a level, trend or range, swing highs and lows, the EMAs,
// volume, support, and when a candle closes. Every answer comes from the
// candles shown (or the clock), never from what price did next.
import type { Candle, ChartMark, GameDef, GameTf, Option, Tone } from "../types";
import { TF_SEC } from "../types";
import {
  atr, chartOf, clockText, emaLast, pctChange, pctText, price, readStructure, roundTo, srLevels, swings, volumeRatio,
  type SrLevel, type Swing,
} from "../helpers";
import { body, isHammer, isShootingStar, lowerWick, upperWick, VOLUME_MIN, VOLUME_STRONG } from "../rules";
import { pricePrecision } from "../../desk/deskData";

// ── Shared bits ─────────────────────────────────────────────────────────────

const qMark = (c: Candle): ChartMark => ({ time: c.time, label: "?", tone: "brand", position: "above" });

/** "+1.20%" / "−0.85%" */
const signedPct = (n: number): string => `${n > 0 ? "+" : ""}${pctText(n)}`;

/** One line on what price did after `from` (the reveal: never scored). */
function moveOutcome(from: Candle, ahead: Candle[]): string | undefined {
  const end = ahead[ahead.length - 1];
  if (!end) return undefined;
  return `Over the next ${ahead.length} candles price went from ${price(from.close)} to ${price(end.close)} (${signedPct(pctChange(from.close, end.close))}).`;
}

/** A share as a whole percent: "18%". */
const pctOf = (share: number): string => `${Math.round(share * 100)}%`;

/** Indices from..to (both included), clamped to the list. */
function indices(from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = Math.max(0, from); i <= to; i++) out.push(i);
  return out;
}

// ── 1. Who won the candle? ──────────────────────────────────────────────────

export type Winner = "buyers" | "sellers" | "tie";
/** A body at most this share of the candle's high-low range is a tie (a doji). */
export const TIE_SHARE = 0.1;
/** Bodies between the tie share and this are too close to call (no question). */
export const CLEAR_SHARE = 0.25;

/** Who won a candle: by its close against its open, a tie when the body is tiny; null when it's a close call. */
export function candleWinner(c: Candle): Winner | null {
  const range = c.high - c.low;
  if (!(range > 0)) return null;
  const share = body(c) / range;
  if (share <= TIE_SHARE) return "tie";
  if (share < CLEAR_SHARE) return null;
  return c.close > c.open ? "buyers" : "sellers";
}

export const buyersOrSellers: GameDef = {
  id: "buyers-or-sellers",
  level: "beginner",
  title: "Who won the candle?",
  skill: "Read who won a candle from where it closed against where it opened.",
  needs: { tf: "1h", history: 60, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    if (n < 10) return null;
    let idx: number | null = null;
    // About one question in five looks for a tie (a doji) anywhere in the last 40 candles.
    if (rng.chance(0.2)) {
      const ties = indices(n - 40, n - 1).filter(i => candleWinner(c[i]!) === "tie");
      if (ties.length > 0) idx = rng.pick(ties);
    }
    if (idx === null) {
      for (const i of rng.shuffle(indices(n - 5, n - 1))) {
        if (candleWinner(c[i]!) !== null) { idx = i; break; }
      }
    }
    if (idx === null) return null;
    const k = c[idx]!;
    const answer = candleWinner(k)!;
    const share = body(k) / (k.high - k.low);
    const shareText = pctOf(share);
    const options: Option[] = [
      {
        id: "buyers",
        label: "Buyers: it closed above its open",
        why: answer === "sellers" ? "It closed below its open (a red candle), so sellers won."
          : answer === "tie" ? `It closed only a hair from its open: the body is just ${shareText} of the candle, too small for either side to win.` : undefined,
      },
      {
        id: "sellers",
        label: "Sellers: it closed below its open",
        why: answer === "buyers" ? "It closed above its open (a green candle), so buyers won."
          : answer === "tie" ? `It closed only a hair from its open: the body is just ${shareText} of the candle, too small for either side to win.` : undefined,
      },
      {
        id: "tie",
        label: "A tie: it closed almost where it opened (a doji)",
        why: answer === "tie" ? undefined : `The body is ${shareText} of the candle's height: far too big for a tie.`,
      },
    ];
    const explain = answer === "tie"
      ? `It opened at ${price(k.open)} and closed at ${price(k.close)}: the body is only ${shareText} of the candle's height (${price(k.low)} to ${price(k.high)}). Buyers and sellers pushed both ways and ended about even: a doji, a sign of indecision.`
      : `It opened at ${price(k.open)} and closed ${answer === "buyers" ? "higher" : "lower"} at ${price(k.close)}: a ${answer === "buyers" ? "green" : "red"} candle whose body is ${shareText} of its height. ${answer === "buyers" ? "Buyers" : "Sellers"} clearly won that candle.`;
    return {
      kind: "choice",
      prompt: "Who won the candle marked “?”? If its body (open to close) is a tenth of the candle's full height or less, call it a tie.",
      chart: chartOf(data, { focus: 45, marks: [qMark(k)] }),
      options,
      answer,
      explain,
      outcome: moveOutcome(c[n - 1]!, data.ahead),
    };
  },
};

// ── 2. Read the wick ────────────────────────────────────────────────────────

export type WickRead = "top" | "bottom" | "none";

/**
 * What a candle's wicks say. top: the upper wick is at least 2× the body and
 * 2× the lower wick (sellers pushed it down); bottom: the mirror (buyers
 * pushed it up); none: both wicks at most half a real body. Anything else is
 * a mixed shape: null.
 */
export function wickRead(c: Candle): WickRead | null {
  if (!(c.high - c.low > 0)) return null;
  const b = body(c), u = upperWick(c), l = lowerWick(c);
  if (u > 0 && u >= 2 * b && u >= 2 * l) return "top";
  if (l > 0 && l >= 2 * b && l >= 2 * u) return "bottom";
  if (b > 0 && u <= 0.5 * b && l <= 0.5 * b) return "none";
  return null;
}

/** "3.4× the body", or "far bigger than the body" when there is almost none. */
function timesBody(w: number, b: number): string {
  return b > 0 && w / b < 100 ? `${(w / b).toFixed(1)}× the body` : "far bigger than the body (there is almost no body)";
}

export const wickReading: GameDef = {
  id: "wick-reading",
  level: "beginner",
  title: "Read the wick",
  skill: "Read a long wick as push-back: the side that pushed price back before the close.",
  needs: { tf: "1h", history: 60, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    if (n < 10) return null;
    // Look through the recent candles for each shape, then ask about one, so all three answers come up.
    const found: Record<WickRead, number[]> = { top: [], bottom: [], none: [] };
    for (const i of indices(n - 20, n - 1)) {
      const r = wickRead(c[i]!);
      if (r) found[r].push(i);
    }
    const want = rng.pick(["top", "bottom", "none"] as const);
    const pool = found[want].length > 0 ? found[want] : [...found.top, ...found.bottom, ...found.none];
    if (pool.length === 0) return null;
    const k = c[rng.pick(pool)]!;
    const answer = wickRead(k)!;
    const b = body(k), u = upperWick(k), l = lowerWick(k);
    const sizes = `upper wick ${price(u)}, body ${price(b)}, lower wick ${price(l)}`;
    let explain: string;
    if (answer === "top") {
      explain = `The upper wick is ${timesBody(u, b)}: price reached ${price(k.high)} but sellers pushed it back down before the close. `
        + (isShootingStar(k)
          ? "With a lower wick of at most half the body, this is the engine's shooting star, one of its sell triggers."
          : "The engine's shooting star (a sell trigger) is this shape with a lower wick of at most half the body.");
    } else if (answer === "bottom") {
      explain = `The lower wick is ${timesBody(l, b)}: price dropped to ${price(k.low)} but buyers pushed it back up before the close. `
        + (isHammer(k)
          ? "With an upper wick of at most half the body, this is the engine's hammer, one of its buy triggers."
          : "The engine's hammer (a buy trigger) is this shape with an upper wick of at most half the body.");
    } else {
      explain = `Both wicks are at most half the body (${sizes}): whoever won this candle met little push-back. The engine's hammer and shooting star need a wick at least 2× the body, so this is neither.`;
    }
    const whyNot = (id: WickRead): string | undefined => {
      if (id === answer) return undefined;
      if (id === "top") return `The upper wick (${price(u)}) isn't long: it needs to be at least twice the body and twice the lower wick.`;
      if (id === "bottom") return `The lower wick (${price(l)}) isn't long: it needs to be at least twice the body and twice the upper wick.`;
      return `One wick is long here (${sizes}), so someone did push back.`;
    };
    return {
      kind: "choice",
      prompt: "Look at the wicks (the thin lines above and below the body) of the candle marked “?”. What do they tell you?",
      chart: chartOf(data, { focus: 40, marks: [qMark(k)] }),
      options: [
        { id: "top", label: "Sellers pushed it down from the top (long upper wick)", why: whyNot("top") },
        { id: "bottom", label: "Buyers pushed it up from the bottom (long lower wick)", why: whyNot("bottom") },
        { id: "none", label: "No real push-back (small wicks)", why: whyNot("none") },
      ],
      answer,
      explain,
      outcome: moveOutcome(c[n - 1]!, data.ahead),
    };
  },
};

// ── 3. Close or just a wick? ────────────────────────────────────────────────

export type LevelRead = "closed" | "wick" | "never";

/**
 * A candle against a level line. side "up": the line is above where it opened
 * (did it close above, only wick above, or never reach it?); "down": the
 * mirror. Null when the close or the wick tip is within 10% of the candle's
 * range from the line (too close to see).
 */
export function closeVsLevel(c: Candle, line: number, side: "up" | "down"): LevelRead | null {
  const r = c.high - c.low;
  if (!(r > 0)) return null;
  const tip = side === "up" ? c.high : c.low;
  if (Math.abs(c.close - line) < 0.1 * r || Math.abs(tip - line) < 0.1 * r) return null;
  if (side === "up") return c.close > line ? "closed" : c.high > line ? "wick" : "never";
  return c.close < line ? "closed" : c.low < line ? "wick" : "never";
}

/** Where a line must sit for each answer (with a margin), or null when the candle's shape can't give it. */
function levelInterval(c: Candle, side: "up" | "down", want: LevelRead): [number, number] | null {
  const r = c.high - c.low;
  const m = 0.15 * r;
  const top = Math.max(c.open, c.close);
  const bottom = Math.min(c.open, c.close);
  let lo: number, hi: number;
  if (side === "up") {
    if (want === "closed") { lo = c.open + m; hi = c.close - m; }
    else if (want === "wick") { lo = top + m; hi = c.high - m; }
    else { lo = c.high + m; hi = c.high + 0.8 * r; }
  } else {
    if (want === "closed") { lo = c.close + m; hi = c.open - m; }
    else if (want === "wick") { lo = c.low + m; hi = bottom - m; }
    else { lo = c.low - 0.8 * r; hi = c.low - m; }
  }
  return hi - lo > 0.02 * r ? [lo, hi] : null;
}

export const closeVsLevelGame: GameDef = {
  id: "close-vs-level",
  level: "beginner",
  title: "Close or just a wick?",
  skill: "Tell a candle that closed past a level from one that only poked a wick through it.",
  needs: { tf: "1h", history: 60, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const all = data.candles;
    if (all.length < 40) return null;
    // Choose the answer first (so all three come up evenly), then the latest candle (of the last 12) whose
    // shape can give it, and a side of it that can. The chart is cut at that candle.
    const want = rng.pick(["closed", "wick", "never"] as const);
    const sidesAt = (i: number): ("up" | "down")[] => {
      const k = all[i]!;
      const a = atr(all.slice(0, i + 1));
      // A tiny candle is hard to read against a line.
      if (!a || !(k.high - k.low >= 0.4 * a)) return [];
      return (["up", "down"] as const).filter(s => levelInterval(k, s, want) !== null);
    };
    const at = indices(all.length - 12, all.length - 1).reverse().find(i => sidesAt(i).length > 0);
    if (at === undefined) return null;
    const c = all.slice(0, at + 1);
    const ahead = [...all.slice(at + 1), ...data.ahead].slice(0, 12);
    const n = c.length;
    const k = c[n - 1]!;
    const r = k.high - k.low;
    const side = rng.pick(sidesAt(at));
    const before = c.slice(n - 21, n - 1);
    // The natural level: the highest high (lowest low) of the 20 candles before, used when it gives the same answer.
    const natural = side === "up" ? Math.max(...before.map(x => x.high)) : Math.min(...before.map(x => x.low));
    const naturalOk = closeVsLevel(k, natural, side) === want
      && (side === "up" ? natural > k.open : natural < k.open)
      && Math.abs(natural - (side === "up" ? k.high : k.low)) <= 3 * r;
    let line: number;
    let named = false;
    if (naturalOk && rng.chance(0.5)) {
      line = natural;
      named = true;
    } else {
      const [lo, hi] = levelInterval(k, side, want)!;
      line = rng.float(lo, hi);
    }
    line = roundTo(line, pricePrecision(line));
    const answer = closeVsLevel(k, line, side);
    if (answer === null) return null;
    const tip = side === "up" ? k.high : k.low;
    // Never show two "equal" prices in the text.
    if (price(line) === price(k.close) || price(line) === price(tip)) return null;
    const above = side === "up";
    const past = above ? "above" : "below";
    const back = above ? "below" : "above";
    const tipWord = above ? "high" : "low";
    const lineText = price(line);
    const explain = answer === "closed"
      ? `It closed at ${price(k.close)}, ${past} the line at ${lineText}. Only the close counts: the engine's triggers (like a close ${past} the last candle's ${tipWord}) look at where a candle closed, never at how far a wick reached.`
      : answer === "wick"
        ? `Its ${tipWord} reached ${price(tip)}, ${past} the line at ${lineText}, but it closed back ${back} at ${price(k.close)}. A wick through a level that closes back is a rejection, not a break: for the engine, price didn't get ${past} the line.`
        : `Its ${tipWord} was ${price(tip)}, still short of the line at ${lineText}. Price didn't even test the level on this candle.`;
    const whyNot = (id: LevelRead): string | undefined => {
      if (id === answer) return undefined;
      if (id === "closed") return answer === "wick"
        ? `Only its wick (to ${price(tip)}) got ${past} the line; it closed at ${price(k.close)}, ${back} it. Only the close counts.`
        : `It never got there: its ${tipWord} was ${price(tip)}.`;
      if (id === "wick") return answer === "closed"
        ? `More than the wick: the close itself (${price(k.close)}) is ${past} the line.`
        : `Not even the wick: its ${tipWord} was ${price(tip)}, short of the line.`;
      return `It did reach the line: its ${tipWord} was ${price(tip)}.`;
    };
    const what = named
      ? `the ${above ? "highest high" : "lowest low"} of the 20 candles before the one marked “?”`
      : `a price level ${above ? "above" : "below"} where the candle marked “?” opened`;
    return {
      kind: "choice",
      prompt: `The dashed line is ${what}. What did that candle do at the line?`,
      chart: {
        tf: data.tf,
        candles: c,
        ahead,
        focus: 40,
        marks: [qMark(k)],
        lines: [{ price: line, label: named ? (above ? "20-candle high" : "20-candle low") : "Level", tone: "warning", dashed: true }],
      },
      options: [
        { id: "closed", label: `Closed ${past} the line`, why: whyNot("closed") },
        { id: "wick", label: `Only a wick went ${past}; it closed ${back}`, why: whyNot("wick") },
        { id: "never", label: "Never reached the line", why: whyNot("never") },
      ],
      answer,
      explain,
      outcome: moveOutcome(k, ahead),
    };
  },
};

// ── 4. Trend or range? ──────────────────────────────────────────────────────

export type TrendRead = "up" | "down" | "range";

export interface TrendQuestion {
  answer: TrendRead;
  /** The last two confirmed swing highs and lows, oldest first. */
  h1: Swing; h2: Swing; l1: Swing; l2: Swing;
  confirmed: Swing[];
}

/**
 * The trend the confirmed swings make (readStructure): needs 4 or more
 * confirmed swings, each pair 0.3 ATR apart or more (so the eye can tell),
 * and a trend the last close hasn't broken; null otherwise.
 */
export function trendRead(candles: Candle[]): TrendQuestion | null {
  const last = candles[candles.length - 1];
  const a = atr(candles);
  if (!last || !a) return null;
  const all = swings(candles);
  const confirmed = all.filter(s => s.confirmed);
  if (confirmed.length < 4) return null;
  const highs = confirmed.filter(s => s.kind === "high").slice(-2);
  const lows = confirmed.filter(s => s.kind === "low").slice(-2);
  if (highs.length < 2 || lows.length < 2) return null;
  const [h1, h2] = highs as [Swing, Swing];
  const [l1, l2] = lows as [Swing, Swing];
  if (Math.abs(h2.price - h1.price) < 0.3 * a || Math.abs(l2.price - l1.price) < 0.3 * a) return null;
  const read = readStructure(all, last.close);
  if (read.trend !== "range" && read.broken) return null;
  return { answer: read.trend, h1, h2, l1, l2, confirmed };
}

const swingTone = (s: Swing): Tone => s.label === "HH" || s.label === "HL" ? "profit" : s.label === "LH" || s.label === "LL" ? "loss" : "muted";

const swingMark = (s: Swing): ChartMark => ({ time: s.time, label: s.label, tone: swingTone(s), position: s.kind === "high" ? "above" : "below" });

export const trendOrRange: GameDef = {
  id: "trend-or-range",
  level: "beginner",
  title: "Trend or range?",
  skill: "Read the trend from the swing highs and lows: both rising, both falling, or neither.",
  needs: { tf: "4h", history: 300, ahead: 30 },
  make(data) {
    if (!data) return null;
    const c = data.candles;
    const t = trendRead(c);
    if (!t) return null;
    const { answer, h1, h2, l1, l2 } = t;
    const shown = t.confirmed.slice(-8);
    const firstIdx = c.findIndex(k => k.time === shown[0]!.time);
    const focus = Math.min(c.length, Math.max(100, c.length - firstIdx + 8));
    const highsUp = h2.price > h1.price;
    const lowsUp = l2.price > l1.price;
    const pairs = `The last two swing highs went ${price(h1.price)} → ${price(h2.price)} (${highsUp ? "higher" : "lower"}) and the last two swing lows ${price(l1.price)} → ${price(l2.price)} (${lowsUp ? "higher" : "lower"}).`;
    const last = c[c.length - 1]!;
    const explain = answer === "up"
      ? `${pairs} Both stepping up is an uptrend: buyers keep winning. Price is still above the last higher low, so the trend isn't broken.`
      : answer === "down"
        ? `${pairs} Both stepping down is a downtrend: sellers keep winning. Price is still below the last lower high, so the trend isn't broken.`
        : `${pairs} They don't step the same way, so there's no clear trend: treat it as a range.`;
    const whyNot = (id: TrendRead): string | undefined => {
      if (id === answer) return undefined;
      if (answer === "range") {
        const fell = highsUp ? "lows" : "highs";
        const rose = highsUp ? "highs" : "lows";
        return id === "up" ? `An uptrend needs both to step up, but the ${fell} stepped down.` : `A downtrend needs both to step down, but the ${rose} stepped up.`;
      }
      if (id === "range") return `Both the highs and the lows step ${answer === "up" ? "up" : "down"}: that's a trend, not a range.`;
      return `The highs and lows are stepping ${answer === "up" ? "up" : "down"}, not ${answer === "up" ? "down" : "up"}.`;
    };
    return {
      kind: "choice",
      prompt: `Compare the last two swing highs and the last two swing lows on this 4h chart (the big turning points, not every wiggle). Is ${data.coin} in an uptrend, a downtrend, or a range?`,
      chart: chartOf(data, { focus, answerMarks: shown.map(swingMark) }),
      options: [
        { id: "up", label: "Uptrend: higher highs and higher lows", why: whyNot("up") },
        { id: "down", label: "Downtrend: lower highs and lower lows", why: whyNot("down") },
        { id: "range", label: "Range: the highs and lows don't both step the same way", why: whyNot("range") },
      ],
      answer,
      explain,
      outcome: moveOutcome(last, data.ahead),
    };
  },
};

// ── 5. Higher or lower swing? ───────────────────────────────────────────────

/** Two swings of the same kind closer than this many ATR are too close to call. */
export const SWING_GAP_ATR = 0.15;

/** B against A (same kind): "higher" or "lower", null when within 0.15 ATR. */
export function swingCompare(a: Swing, b: Swing, atrValue: number): "higher" | "lower" | null {
  const d = b.price - a.price;
  if (Math.abs(d) < SWING_GAP_ATR * atrValue) return null;
  return d > 0 ? "higher" : "lower";
}

export const higherOrLower: GameDef = {
  id: "higher-or-lower",
  level: "beginner",
  title: "Higher or lower swing?",
  skill: "Compare two swing highs (or lows) and say what the change means for the trend.",
  needs: { tf: "1h", history: 200, ahead: 24 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const a = atr(c);
    if (!a) return null;
    const confirmed = swings(c).filter(s => s.confirmed);
    // Neighbouring swings of the same kind (A then B), the last 3 of each kind, clear enough to compare.
    const pairsOf = (kind: "high" | "low") => {
      const list = confirmed.filter(s => s.kind === kind);
      const out: [Swing, Swing][] = [];
      for (let j = 1; j < list.length; j++) if (swingCompare(list[j - 1]!, list[j]!, a)) out.push([list[j - 1]!, list[j]!]);
      return out.slice(-3);
    };
    const highs = pairsOf("high");
    const lows = pairsOf("low");
    const kind = rng.pick(["high", "low"] as const);
    const pool = kind === "high" ? (highs.length > 0 ? highs : lows) : (lows.length > 0 ? lows : highs);
    if (pool.length === 0) return null;
    const [A, B] = rng.pick(pool);
    const answer = swingCompare(A, B, a)!;
    const isHigh = A.kind === "high";
    const word = isHigh ? "high" : "low";
    const idxA = c.findIndex(k => k.time === A.time);
    const focus = Math.min(c.length, Math.max(40, c.length - idxA + 10));
    const pa = price(A.price), pb = price(B.price);
    const explain = isHigh
      ? answer === "higher"
        ? `B's high (${pb}) is above A's (${pa}): buyers pushed further than last time. Higher highs, with higher lows, make an uptrend.`
        : `B's high (${pb}) is below A's (${pa}): buyers couldn't get back to the last top. Lower highs, with lower lows, make a downtrend.`
      : answer === "higher"
        ? `B's low (${pb}) is above A's (${pa}): sellers couldn't push as low as last time, so buyers stepped in sooner. Higher lows, with higher highs, make an uptrend.`
        : `B's low (${pb}) is below A's (${pa}): sellers pushed past the last bottom. Lower lows, with lower highs, make a downtrend.`;
    return {
      kind: "choice",
      prompt: `“A” and “B” mark two swing ${word}s (turning points at the ${isHigh ? "top" : "bottom"}), A first. Is B a higher ${word} or a lower ${word}?`,
      chart: chartOf(data, {
        focus,
        marks: [
          { time: A.time, label: "A", tone: "brand", position: isHigh ? "above" : "below" },
          { time: B.time, label: "B", tone: "brand", position: isHigh ? "above" : "below" },
        ],
        answerLines: [{ price: A.price, label: `A ${word}`, tone: "muted", dashed: true }],
      }),
      options: [
        { id: "higher", label: `B is a higher ${word}`, why: answer === "lower" ? `B's ${word} (${pb}) is below A's (${pa}).` : undefined },
        { id: "lower", label: `B is a lower ${word}`, why: answer === "higher" ? `B's ${word} (${pb}) is above A's (${pa}).` : undefined },
      ],
      answer,
      explain,
      outcome: moveOutcome(c[c.length - 1]!, data.ahead),
    };
  },
};

// ── 6. Which EMA is on top? ─────────────────────────────────────────────────

/** EMAs closer than this many ATR are too close to call. */
export const EMA_GAP_ATR = 0.15;

/** "up" when EMA 20 is above EMA 50, "down" below, null when within 0.15 ATR. */
export function emaOrder(e20: number, e50: number, atrValue: number): "up" | "down" | null {
  if (Math.abs(e20 - e50) < EMA_GAP_ATR * atrValue) return null;
  return e20 > e50 ? "up" : "down";
}

export const emaOrderGame: GameDef = {
  id: "ema-order",
  level: "beginner",
  title: "Which EMA is on top?",
  skill: "Read the 1h trend the desk's way: EMA 20 above EMA 50 is up, below is down.",
  needs: { tf: "1h", history: 220, ahead: 24 },
  make(data) {
    if (!data) return null;
    const c = data.candles;
    const e20 = emaLast(c, 20), e50 = emaLast(c, 50), a = atr(c);
    if (e20 === null || e50 === null || !a) return null;
    const answer = emaOrder(e20, e50, a);
    if (!answer) return null;
    const up = answer === "up";
    return {
      kind: "choice",
      prompt: "An EMA is a moving average of the closes that weights recent candles more. At the last candle, which is on top: the blue EMA 20 or the violet EMA 50?",
      chart: chartOf(data, { emas: [20, 50] }),
      options: [
        { id: "up", label: "EMA 20 above EMA 50: buyers in control (uptrend)", why: up ? undefined : `At the last candle EMA 20 (${price(e20)}) is below EMA 50 (${price(e50)}).` },
        { id: "down", label: "EMA 20 below EMA 50: sellers in control (downtrend)", why: up ? `At the last candle EMA 20 (${price(e20)}) is above EMA 50 (${price(e50)}).` : undefined },
      ],
      answer,
      explain: `EMA 20 is at ${price(e20)} and EMA 50 at ${price(e50)}: the fast average is ${up ? "above" : "below"} the slow one, so recent prices are ${up ? "higher" : "lower"} than older ones. The desk's 1h rule: buys only when EMA 20 is above EMA 50, sells only when it's below.`,
      outcome: moveOutcome(c[c.length - 1]!, data.ahead),
    };
  },
};

// ── 7. Above or below the EMAs? ─────────────────────────────────────────────

export type EmaPlace = "above" | "between" | "below";

/** Where a close sits against EMA 20 and EMA 50; null when it's within 0.1 ATR of either, or they're within 0.1 ATR of each other. */
export function priceVsEmas(close: number, e20: number, e50: number, atrValue: number): EmaPlace | null {
  const m = 0.1 * atrValue;
  if (Math.abs(close - e20) < m || Math.abs(close - e50) < m || Math.abs(e20 - e50) < m) return null;
  if (close > e20 && close > e50) return "above";
  if (close < e20 && close < e50) return "below";
  return "between";
}

export const priceVsEmasGame: GameDef = {
  id: "price-vs-emas",
  level: "beginner",
  title: "Above or below the EMAs?",
  skill: "See where price sits against the two EMAs and what that says about who's in control.",
  needs: { tf: "1h", history: 220, ahead: 24 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    // "Between" is the rarest: find recent closes in each place, pick the place first, then cut the chart there.
    const read = (i: number) => {
      const upTo = c.slice(0, i + 1);
      const e20 = emaLast(upTo, 20), e50 = emaLast(upTo, 50), a = atr(upTo);
      if (e20 === null || e50 === null || !a) return null;
      const place = priceVsEmas(c[i]!.close, e20, e50, a);
      return place ? { e20, e50, place } : null;
    };
    const found: Record<EmaPlace, number[]> = { above: [], between: [], below: [] };
    for (const i of indices(Math.max(60, n - 48), n - 1)) {
      const r = read(i);
      if (r) found[r.place].push(i);
    }
    const want = rng.pick(["above", "between", "below"] as const);
    const pool = found[want].length > 0 ? found[want] : [...found.above, ...found.between, ...found.below];
    if (pool.length === 0) return null;
    const i = rng.pick(pool);
    const { e20, e50, place: answer } = read(i)!;
    const shown = c.slice(0, i + 1);
    const ahead = [...c.slice(i + 1), ...data.ahead].slice(0, 24);
    const k = c[i]!;
    const side = (e: number) => (k.close > e ? "above" : "below");
    const facts = `The last close was ${price(k.close)}: ${side(e20)} EMA 20 (${price(e20)}) and ${side(e50)} EMA 50 (${price(e50)}).`;
    const meaning = answer === "above"
      ? "Above both, buyers are in control right now."
      : answer === "below"
        ? "Below both, sellers are in control right now."
        : e20 > e50
          ? "Between them in an uptrend (EMA 20 on top) is a pullback: the desk looks for buy entries when price comes back near the EMAs."
          : "Between them in a downtrend (EMA 20 below) is a bounce: the desk looks for sell entries when price comes back near the EMAs.";
    const whyNot = (id: EmaPlace) => (id === answer ? undefined : facts);
    return {
      kind: "choice",
      prompt: "Where did the last candle (marked “?”) close compared with the blue EMA 20 and the violet EMA 50?",
      chart: { tf: data.tf, candles: shown, ahead, emas: [20, 50], marks: [qMark(k)] },
      options: [
        { id: "above", label: "Above both EMAs", why: whyNot("above") },
        { id: "between", label: "Between the two EMAs", why: whyNot("between") },
        { id: "below", label: "Below both EMAs", why: whyNot("below") },
      ],
      answer,
      explain: `${facts} ${meaning}`,
      outcome: moveOutcome(k, ahead),
    };
  },
};

// ── 8. Busy or quiet candle? ────────────────────────────────────────────────

export type VolBand = "busy" | "normal" | "quiet";

/** Volume ÷ the 20 candles before: busy at 1.5× or more, normal at 0.8–1.25×, quiet at 0.6× or less; the gaps are null. */
export function volumeBand(ratio: number): VolBand | null {
  if (ratio >= 1.5) return "busy";
  if (ratio >= 0.8 && ratio <= 1.25) return "normal";
  if (ratio <= 0.6) return "quiet";
  return null;
}

/** "12,345" / "84.20" */
function volText(v: number): string {
  return v.toLocaleString("en-US", { minimumFractionDigits: v >= 100 ? 0 : 2, maximumFractionDigits: v >= 100 ? 0 : 2 });
}

export const volumeRead: GameDef = {
  id: "volume-read",
  level: "beginner",
  title: "Busy or quiet candle?",
  skill: "Judge a candle's volume against the 20 candles before it, the way the engine does.",
  needs: { tf: "1h", history: 120, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const n = c.length;
    // Find recent candles in each band, then cut the chart at one of them.
    const found: Record<VolBand, number[]> = { busy: [], normal: [], quiet: [] };
    for (const i of indices(Math.max(40, n - 60), n - 1)) {
      const ratio = volumeRatio(c.slice(i - 20, i + 1));
      const band = ratio === null ? null : volumeBand(ratio);
      if (band) found[band].push(i);
    }
    const want = rng.pick(["busy", "normal", "quiet"] as const);
    const pool = found[want].length > 0 ? found[want] : [...found.busy, ...found.normal, ...found.quiet];
    if (pool.length === 0) return null;
    const i = rng.pick(pool);
    const shown = c.slice(Math.max(0, i + 1 - 80), i + 1);
    const ahead = [...c.slice(i + 1), ...data.ahead].slice(0, 12);
    const k = shown[shown.length - 1]!;
    const ratio = volumeRatio(shown);
    const answer = ratio === null ? null : volumeBand(ratio);
    if (ratio === null || answer === null) return null;
    const avg = k.volume / ratio;
    const rt = `${ratio.toFixed(2)}×`;
    const verdict = ratio < VOLUME_MIN ? "be too quiet to count" : ratio >= VOLUME_STRONG ? "count as strong" : "count, but not as strong";
    const bandWords: Record<VolBand, string> = { busy: "1.5× or more", normal: "between 0.8× and 1.25×", quiet: "0.6× or less" };
    const whyNot = (id: VolBand) => (id === answer ? undefined : `It was ${rt} normal, not ${bandWords[id]}.`);
    return {
      kind: "choice",
      prompt: "Look at the volume bar (how much was traded) under the candle marked “?”. Compared with the 20 candles before it, was it busy, normal or quiet?",
      chart: { tf: data.tf, candles: shown, ahead, focus: 40, volume: true, marks: [qMark(k)] },
      options: [
        { id: "busy", label: "Busy: 1.5× normal or more", why: whyNot("busy") },
        { id: "normal", label: "About normal (0.8×–1.25×)", why: whyNot("normal") },
        { id: "quiet", label: "Quiet: 0.6× normal or less", why: whyNot("quiet") },
      ],
      answer,
      explain: `Its volume was ${volText(k.volume)} and the 20 candles before averaged ${volText(avg)}: ${volText(k.volume)} ÷ ${volText(avg)} = ${rt} normal. On a trigger candle the engine needs at least ${VOLUME_MIN}× (${VOLUME_STRONG}× or more is strong), so this one would ${verdict}.`,
      outcome: moveOutcome(k, ahead),
    };
  },
};

// ── 9. Find the support ─────────────────────────────────────────────────────

/** Candles that fill the view: the support's latest touch must be among them. */
const SUPPORT_FOCUS = 120;

export interface SupportPick {
  level: SrLevel;
  atr: number;
  close: number;
  accept: { from: number; to: number };
}

/**
 * The nearest support below the last close (srLevels), accepted within half
 * an ATR either side (never above the close). Null when there is none, when
 * it's within 0.5 ATR of the close or more than 6 ATR away, when its latest
 * touch is out of view, or when a fresh dip in the last 2 candles (too new to
 * count) could pass for a nearer support.
 */
export function nearestSupport(candles: Candle[]): SupportPick | null {
  const n = candles.length;
  const last = candles[n - 1];
  const a = atr(candles);
  if (!last || !a) return null;
  const level = srLevels(candles, last.close, 3)
    .filter(l => l.kind === "support")
    .sort((x, y) => y.price - x.price)[0];
  if (!level) return null;
  const d = last.close - level.price;
  if (d < 0.5 * a || d > 6 * a) return null;
  const latest = level.touches[level.touches.length - 1]!;
  if (candles.findIndex(k => k.time === latest.time) < n - SUPPORT_FOCUS) return null;
  const recentLow = Math.min(...candles.slice(-2).map(k => k.low));
  if (recentLow > level.price + 0.5 * a && recentLow < last.close - 0.5 * a) return null;
  return { level, atr: a, close: last.close, accept: { from: level.price - 0.5 * a, to: Math.min(level.price + 0.5 * a, last.close) } };
}

export const supportBelow: GameDef = {
  id: "support-below",
  level: "beginner",
  title: "Find the support",
  skill: "Find the nearest support: the closest price below where the market turned before.",
  needs: { tf: "4h", history: 200, ahead: 30 },
  make(data, rng) {
    if (!data) return null;
    const all = data.candles;
    const n = all.length;
    // The last moment when it makes a clear question, else one of the 24 before it (the chart is cut there).
    let i = n - 1;
    let pick = nearestSupport(all);
    if (!pick) {
      const ok = indices(n - 25, n - 2).filter(j => nearestSupport(all.slice(0, j + 1)) !== null);
      if (ok.length === 0) return null;
      i = rng.pick(ok);
      pick = nearestSupport(all.slice(0, i + 1))!;
    }
    const c = all.slice(0, i + 1);
    const ahead = [...all.slice(i + 1), ...data.ahead].slice(0, 30);
    const { level, atr: a, close, accept } = pick;
    const byTime = new Map(c.map(k => [k.time, k]));
    const lows = level.touches.filter(t => byTime.get(t.time)?.low === t.price);
    const highs = level.touches.length - lows.length;
    const touchMarks: ChartMark[] = level.touches.map(t => ({
      time: t.time, label: "S", tone: "profit", position: byTime.get(t.time)?.low === t.price ? "below" : "above",
    }));
    const count = level.touches.length;
    const times = count === 1 ? "once" : count === 2 ? "twice" : `${count} times`;
    const kinds = [
      lows.length > 0 ? `${lows.length === 1 ? "a low" : `${lows.length} lows`} it bounced up from` : "",
      highs > 0 ? `${highs === 1 ? "an old high" : `${highs} old highs`} it later broke above` : "",
    ].filter(Boolean).join(" and ");
    const who = lows.length > 0 ? "so buyers have stepped in at that price" : "and an old high that price broke often turns into support";
    const d = (close - level.price) / a;
    let outcome: string | undefined;
    if (ahead.length > 0) {
      const minLow = Math.min(...ahead.map(k => k.low));
      const what = minLow > accept.to ? "price didn't come back down to it"
        : minLow >= accept.from ? "price came back to it and it held"
          : "price broke below it";
      outcome = `In the next ${ahead.length} candles the lowest price was ${price(minLow)}: ${what}.`;
    }
    return {
      kind: "price",
      prompt: `Click the nearest support below the current price (${price(close)}): the closest price under it where the market turned before.`,
      chart: {
        tf: data.tf,
        candles: c,
        ahead,
        focus: SUPPORT_FOCUS,
        answerLines: [{ price: level.price, label: "Support", tone: "profit" }],
        answerMarks: touchMarks,
        answerZones: [{ from: accept.from, to: accept.to, tone: "profit" }],
      },
      answer: level.price,
      accept,
      explain: `The nearest support is ${price(level.price)}: price turned there ${times} before (${kinds}), ${who}. It's ${d.toFixed(1)} ATR (the average candle size, ${price(a)}) below the close; anywhere within half an ATR of it counts.`,
      outcome,
    };
  },
};

// ── 10. When does it close? ─────────────────────────────────────────────────

const CLOCK_TFS: { tf: GameTf; words: string; name: string }[] = [
  { tf: "15m", words: "15 minutes", name: "15-minute" },
  { tf: "1h", words: "1 hour", name: "1-hour" },
  { tf: "4h", words: "4 hours", name: "4-hour" },
];

/** A candle's close: its open plus one timeframe. */
export const closeOf = (openSec: number, tf: GameTf): number => openSec + TF_SEC[tf];

const utc = (sec: number) => `${clockText(sec)} UTC`;

/** 1 Jan 2026 00:00 UTC. */
const DAY0 = 1_767_225_600;

const TRIGGER_SHAPES = ["a hammer", "a bullish engulfing candle", "a shooting star", "a bearish engulfing candle", "a close above the last candle's high"] as const;

export const candleCloseTime: GameDef = {
  id: "candle-close-time",
  level: "beginner",
  title: "When does it close?",
  skill: "Know when a candle closes, because only a closed candle counts.",
  make(_data, rng) {
    const t = rng.pick(CLOCK_TFS);
    const tfSec = TF_SEC[t.tf];
    const minutes = tfSec / 60;
    const day = DAY0 + rng.int(0, 270) * 86_400;
    const open = day + rng.int(0, 86_400 / tfSec - 1) * tfSec;
    const close = closeOf(open, t.tf);
    const facts = (now: number) => [
      { label: "Timeframe", value: t.tf },
      { label: "Candle opened", value: utc(open) },
      { label: "Time now", value: utc(now) },
    ];
    const rule = `A ${t.tf} candle lasts ${t.words}: the one that opened at ${utc(open)} closes at ${utc(close)}.`;

    if (rng.chance(0.6)) {
      // When does it close?
      const now = open + rng.int(1, minutes - 1) * 60;
      const wrong: { sec: number; why: string }[] = [
        { sec: open, why: "That's when it opened. It closes one timeframe later." },
        { sec: close + tfSec, why: "That's one candle too late: the next candle closes then." },
        ...CLOCK_TFS.filter(o => o.tf !== t.tf).map(o => ({
          sec: open + TF_SEC[o.tf], why: `That's when a ${o.name} candle opened at ${utc(open)} would close, not a ${t.name} one.`,
        })),
      ];
      const right = utc(close);
      const seen = new Set([right]);
      const picks = rng.shuffle(wrong).filter(w => {
        const label = utc(w.sec);
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      }).slice(0, 3);
      // Neutral ids (a, b, c, d) in the shown order, so the id never gives the answer away.
      const shuffled = rng.shuffle([{ label: right, why: undefined as string | undefined }, ...picks.map(w => ({ label: utc(w.sec), why: w.why }))]);
      const options: Option[] = shuffled.map((o, i) => ({ id: "abcd"[i]!, label: o.label, why: o.why }));
      return {
        kind: "choice",
        prompt: `It's ${utc(now)} and you're watching the ${t.tf} candle that opened at ${utc(open)}. When does it close (the moment the engine checks it)?`,
        facts: facts(now),
        options,
        answer: options.find(o => o.label === right)!.id,
        explain: `${rule} Until then its high, low and close can still change, so the engine only reads a candle once it has closed.`,
      };
    }

    // Can you act on it now?
    const closed = rng.chance(0.5);
    const now = closed ? close + rng.int(1, Math.min(minutes - 1, 50)) * 60 : open + rng.int(1, minutes - 1) * 60;
    const shape = rng.pick(TRIGGER_SHAPES);
    return {
      kind: "choice",
      prompt: `It's ${utc(now)}. The ${t.tf} candle that opened at ${utc(open)} looks like ${shape}. Can you act on it now?`,
      facts: facts(now),
      options: [
        { id: "yes", label: "Yes: it has closed", why: closed ? undefined : `It's still forming until ${utc(close)}: its shape can still change.` },
        { id: "no", label: `No: wait for the close at ${utc(close)}`, why: closed ? `It already closed at ${utc(close)}; at ${utc(now)} its shape is final.` : undefined },
      ],
      answer: closed ? "yes" : "no",
      explain: closed
        ? `${rule} It's now ${utc(now)}, so the candle has closed and its shape is final: only a closed candle counts, and this one has.`
        : `${rule} At ${utc(now)} it's still forming, and ${shape} can still turn into something else before the close. Only a closed candle counts.`,
    };
  },
};

export const GAMES: GameDef[] = [
  buyersOrSellers,
  wickReading,
  closeVsLevelGame,
  trendOrRange,
  higherOrLower,
  emaOrderGame,
  priceVsEmasGame,
  volumeRead,
  supportBelow,
  candleCloseTime,
];
